import { assertNever } from '../../exhaustive';
import { COREDUMP_RESET, PANEL_STATE, RCU_STATUS_TYPE } from '../catalog/ack';
import type { AckStatus } from '../catalog/ack';
import { RCU_GROUP } from '../catalog/protocol';
import type { DinModuleId } from '../catalog/protocol';
import { addChunk } from './coredump';
import type { CoredumpTransfer } from './coredump';
import type {
  CoredumpAckReading,
  CoredumpInfoReading,
  DaliAckReading,
  LoggingAckReading,
  LoggingStatusReading,
  LogsAckReading,
  MappingEntryReading,
  PanelEvent,
  RcuReading,
  S3AckReading,
  StatusReading,
} from './readings';

/**
 * What the console currently knows about the device, folded from every message
 * that has arrived.
 *
 * Panels read this instead of the event feed: the feed is history, this is the
 * present. Each fact is keyed the way it is addressed (group, relay / module,
 * address, channel), so a panel can look up exactly the tile it is drawing, and
 * anything that has not been read yet is simply absent.
 */
export interface OtaProgressState {
  readonly progress: number;
  readonly status: AckStatus | null;
}

export interface S3ImportState {
  readonly progress: number;
  readonly ack: S3AckReading | null;
}

export interface LiveState {
  readonly status: StatusReading | null;
  /** Relay values by `group:relay`. */
  readonly relays: Readonly<Record<string, number>>;
  /** The last `getallrcurelays` reply, indexed from relay 1. */
  readonly allRelays: readonly number[] | null;
  /** Relay command results by `group:relay`. */
  readonly relayAcks: Readonly<Record<string, AckStatus>>;
  /**
   * When each relay value was reported, by the same key as `relays`. A guest
   * action made after this moment has not been answered yet, so the control
   * keeps showing what the guest asked for instead of snapping back to a
   * reading that predates it.
   */
  readonly relayAt: Readonly<Record<string, string>>;
  /** DIN channel values by `module:addr:channel`. */
  readonly din: Readonly<Record<string, number>>;
  /** When each DIN value was reported, by the same key as `din`. */
  readonly dinAt: Readonly<Record<string, string>>;
  /** Last `dinstatusall` per module, keyed `module:addr`. */
  readonly dinAll: Readonly<Record<string, readonly number[]>>;
  /** DIN command results by `module:addr:channel`. */
  readonly dinAcks: Readonly<Record<string, AckStatus>>;
  /** Last DALI answer per `master:address`. */
  readonly dali: Readonly<Record<string, DaliAckReading>>;
  readonly panel: PanelEvent | null;
  /** When the current panel watch started, for its five-minute countdown. */
  readonly panelStartedAt: string | null;
  readonly mappings: readonly MappingEntryReading[];
  readonly mappingsAt: string | null;
  readonly ledMappings: readonly MappingEntryReading[];
  readonly ledMappingsAt: string | null;
  readonly coredump: CoredumpInfoReading | null;
  readonly coredumpTransfer: CoredumpTransfer | null;
  readonly coredumpAck: CoredumpAckReading | null;
  readonly logging: LoggingStatusReading | null;
  readonly loggingAck: LoggingAckReading | null;
  readonly logs: LogsAckReading | null;
  readonly ota: OtaProgressState;
  readonly s3: S3ImportState;
  /** Last result per reply without its own home, e.g. `mappingack`. */
  readonly results: Readonly<Record<string, AckStatus>>;
}

export const EMPTY_LIVE: LiveState = {
  status: null,
  relays: {},
  allRelays: null,
  relayAcks: {},
  relayAt: {},
  din: {},
  dinAll: {},
  dinAcks: {},
  dinAt: {},
  dali: {},
  panel: null,
  panelStartedAt: null,
  mappings: [],
  mappingsAt: null,
  ledMappings: [],
  ledMappingsAt: null,
  coredump: null,
  coredumpTransfer: null,
  coredumpAck: null,
  logging: null,
  loggingAck: null,
  logs: null,
  ota: { progress: 0, status: null },
  s3: { progress: 0, ack: null },
  results: {},
};

const key = (...parts: readonly (string | number | null)[]): string => parts.join(':');

const withResult = (
  results: Readonly<Record<string, AckStatus>>,
  statusType: string,
  status: AckStatus,
): Readonly<Record<string, AckStatus>> => ({ ...results, [statusType]: status });

/** A cleared coredump record, from a reply that carries no details of its own. */
const absentCoredump = (): CoredumpInfoReading => ({
  kind: 'coredumpInfo',
  present: false,
  crashSeq: 0,
  reset: COREDUMP_RESET.UNKNOWN,
  dumpId: '',
  size: 0,
  uploads: 0,
  task: '',
  pc: '',
});

/** Relay key for a report that arrived without `groupid`, e.g. a panel operation. */
const reportGroup = (group: number | null): number => group ?? RCU_GROUP.DEFAULT;

/** Folds one reading into the live state. Pure, so a panel can be tested alone. */
export const applyReading = (live: LiveState, reading: RcuReading, at: string): LiveState => {
  switch (reading.kind) {
    case 'relayStatus': {
      const relayKey = key(reportGroup(reading.group), reading.relay);
      return {
        ...live,
        relays: { ...live.relays, [relayKey]: reading.value },
        relayAt: { ...live.relayAt, [relayKey]: at },
      };
    }

    case 'allRelays': {
      const relays = { ...live.relays };
      const relayAt = { ...live.relayAt };
      reading.values.forEach((value, index) => {
        // The array is relay 1 onwards, of the group ordinary use means.
        const relayKey = key(RCU_GROUP.DEFAULT, index + 1);
        relays[relayKey] = value;
        relayAt[relayKey] = at;
      });
      return { ...live, relays, relayAt, allRelays: reading.values };
    }

    case 'relayAck':
      return {
        ...live,
        relayAcks: { ...live.relayAcks, [key(reading.group, reading.relay)]: reading.status },
      };

    case 'dinStatus': {
      if (reading.module === null) return live;
      const dinKey = key(reading.module, reading.addr, reading.channel);
      return {
        ...live,
        din: { ...live.din, [dinKey]: reading.value },
        dinAt: { ...live.dinAt, [dinKey]: at },
      };
    }

    case 'dinStatusAll': {
      if (reading.module === null) return live;
      const moduleKey = dinModuleKey(reading.module, reading.addr);
      const din: Record<string, number> = { ...live.din };
      const dinAt: Record<string, string> = { ...live.dinAt };
      reading.values.forEach((value, index) => {
        const channelKey = key(reading.module, reading.addr, index);
        din[channelKey] = value;
        dinAt[channelKey] = at;
      });
      return {
        ...live,
        din,
        dinAt,
        dinAll: { ...live.dinAll, [moduleKey]: reading.values },
      };
    }

    case 'dinAck':
      return {
        ...live,
        dinAcks: {
          ...live.dinAcks,
          [key(reading.module === null ? 'unknown' : reading.module, reading.addr, reading.channel)]:
            reading.status,
        },
      };

    case 'daliOperateAck':
      /** The submit result; the bus's own answer follows as a `daliAck`. */
      return {
        ...live,
        results: withResult(live.results, RCU_STATUS_TYPE.DALI_OPERATE, reading.status),
      };

    case 'daliAck':
      return { ...live, dali: { ...live.dali, [key(reading.master, reading.address)]: reading } };

    case 'panel':
      return {
        ...live,
        panel: reading.event,
        panelStartedAt: reading.event.state === PANEL_STATE.STARTED ? at : live.panelStartedAt,
      };

    case 'status':
      return { ...live, status: reading };

    case 'mappingRead':
      return { ...live, mappings: reading.entries, mappingsAt: at };

    case 'ledMappingRead':
      return { ...live, ledMappings: reading.entries, ledMappingsAt: at };

    case 'coredumpInfo':
      return { ...live, coredump: reading };

    case 'coredumpChunk':
      return { ...live, coredumpTransfer: addChunk(live.coredumpTransfer, reading) };

    case 'coredumpAck':
      return {
        ...live,
        coredumpAck: reading,
        /** Erasing clears the transfer; a failure leaves whatever arrived. */
        coredumpTransfer: reading.erased ? null : live.coredumpTransfer,
        coredump:
          reading.erased && live.coredump !== null
            ? { ...live.coredump, present: false }
            : (live.coredump ?? (reading.erased ? absentCoredump() : null)),
      };

    case 'loggingStatus':
      return { ...live, logging: reading };

    case 'loggingAck':
      return { ...live, loggingAck: reading };

    case 'logsAck':
      return {
        ...live,
        logs: reading,
        results: withResult(live.results, RCU_STATUS_TYPE.LOGS_ACK, reading.status),
      };

    case 'otaProgress':
      return { ...live, ota: { ...live.ota, progress: reading.progress } };

    case 'otaAck':
      return { ...live, ota: { ...live.ota, status: reading.status } };

    case 's3Progress':
      return { ...live, s3: { ...live.s3, progress: reading.progress } };

    case 's3Ack':
      return { ...live, s3: { ...live.s3, ack: reading } };

    case 'commandResult':
      return { ...live, results: withResult(live.results, reading.statusType, reading.status) };

    /** Reported as events only: they change no view this state holds. */
    case 'notify':
    case 'rxFrame':
    case 'unknown':
      return live;

    default:
      return assertNever(reading);
  }
};

/** The relay key a panel uses for one tile. */
export const relayKey = (group: number, relay: number): string => key(group, relay);

/** The DIN key a panel uses for one channel, in the module's own numbering. */
export const dinKey = (module: DinModuleId, addr: number, channel: number): string =>
  key(module, addr, channel);

/** The DIN key a panel uses for a whole module's last read. */
export const dinModuleKey = (module: DinModuleId, addr: number): string => key(module, addr);

/** The DALI key a panel uses for one address. */
export const daliKey = (master: number, address: number): string => key(master, address);
